import TypedSelect from "common/ui/TypedSelect";
import Offer from "domain/offer";
import React, { useContext, useState } from "react";
import { Row, Col, Form } from 'react-bootstrap';
import FcmAccount from "features/fcmAccounts/fcmAccount";
import NumberEditor from "common/ui/NumberEditor";
import calculateNumberOfContracts from './contractCalculator';
import { OrderType } from "domain/orderType";
import { localizeOfferContract as localize, localizeCommon } from "features/localization/localizeService";
import { HedgingSettings } from "features/settings/hedging/hedgingSettings";
import { findHedgeSetting } from "features/settings/hedging/helpers";
import { getPositionSetting } from "./helpers";
import { PositionSetting } from "domain/positionSetting";
import { FormContext } from "common/useFormContext";

type OfferHedgeSettingsProps = {
    offer: Offer;
    fcmAccounts: FcmAccount[];
    onOfferUpdated(offer: NullishPartial<Offer>, deliveryMethodUpdated?: boolean): void;
    isPlaceHedgeOrderChecked?: boolean;
    setPlaceHedgeOrderChecked(value: boolean): void;
    hedgingSettings: HedgingSettings[];
    positionSettings: PositionSetting[];
};

const OfferHedgeSettings: React.FC<OfferHedgeSettingsProps> = (props: OfferHedgeSettingsProps) => {

    const [showHedgeSettings, setShowHedgeSettings] = useState(props.isPlaceHedgeOrderChecked);
    const formContext = useContext(FormContext);

    if (props.offer == null) return null;

    return <>
        {props.offer.orderType === OrderType.ManualEntry && <Form.Group as={Row} data-testid="hedge-selection-manual-entry-section">
            <Form.Label column>{localize("Details_Label_RecordHedgeOrder")}</Form.Label>
            <Col md="8">
                <Form.Check type="switch" id="placeLimitOrder" label="" className="d-inline-block" checked={showHedgeSettings}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                        setShowHedgeSettings(event.target.checked);
                        props.setPlaceHedgeOrderChecked(event.target.checked);
                        formContext.reset();
                    }} />
            </Col>

        </Form.Group>}
        {showHedgeSettings && <HedgeSettingDetails {...props} />}
    </>
}

const HedgeSettingDetails: React.FC<OfferHedgeSettingsProps> = props => {

    const maxContracts = props.offer.orderType === OrderType.ManualEntry ? 500 : 100;
    const isIceberg = props.offer.orderType === OrderType.Iceberg;

    return <>
        <Form.Group as={Row} data-testid="hedge-fcm-acct-section">
            <Form.Label column>{localize("Details_Label_FcmAccount")}</Form.Label>
            <Col md="8">
                <TypedSelect
                    required
                    options={props.fcmAccounts ?? []}
                    value={props.offer.fcmAccount}
                    valueGetter={m => m.id}
                    textGetter={m => m.accountNumber}
                    showNullRow
                    nullRowText={localizeCommon("Value_Select")}
                    onValueSelected={m => props.onOfferUpdated({ fcmAccount: m })} />
            </Col>
        </Form.Group>
        {!isIceberg && (props.offer.commodity?.miniContractSize || props.offer.futuresContract?.commodity?.miniContractSize) && <Form.Group as={Row} data-testid="hedge-use-mini-contract-section">
            <Form.Label column>{localize("Details_Label_UseMiniContracts")}</Form.Label>
            <Col md="8" className="pt-1">
                <Form.Check type="switch" id="at-use-minis" label="" className="d-inline-block" checked={props.offer.useMini}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => props.onOfferUpdated({
                        useMini: event.target.checked,
                        numberOfContracts: calculateNumberOfContracts(
                            props.offer.deliveryPeriod?.commodity,
                            props.offer.quantity,
                            event.target.checked,
                            findHedgeSetting(props.hedgingSettings, props.offer),
                            getPositionSetting(props.positionSettings, props.offer))
                    })} />
            </Col>
        </Form.Group>}
        <Form.Group as={Row} data-testid="hedge-contracts-section">
            <Form.Label column>{localize("Details_Label_Contracts")}</Form.Label>
            <Col md="8">
                <NumberEditor className="form-control"
                    required
                    min={1}
                    isInvalidMessage={localize("Details_Validation_ContractsOutOfRange", { min: 1, max: maxContracts })}
                    max={maxContracts}
                    step={1}
                    value={props.offer.numberOfContracts}
                    onValueChange={value => props.onOfferUpdated({ numberOfContracts: value })}
                    validationName="numberOfContracts"
                />
            </Col>
        </Form.Group>
        {isIceberg && (
            <Form.Group as={Row} data-testid="hedge-max-show-section">
                <Form.Label column>{localize("Details_Label_MaxShow")}</Form.Label>
                <Col md="8">
                    <NumberEditor
                        className="form-control"
                        validationName="maxShow"
                        required
                        min={1}
                        max={props.offer.numberOfContracts}
                        isInvalidMessage={localize("Details_Validation_MaxShowOutOfRange", { min: 1, max: props.offer.numberOfContracts })}
                        step={1}
                        value={props.offer.maxShow}
                        onValueChange={value => props.onOfferUpdated({ maxShow: value })}
                    />
                </Col>
            </Form.Group>
        )}
        <Form.Group as={Row} data-testid="hedge-comments-section">
            <Form.Label column>{localize("Details_Label_Comments")}</Form.Label>
            <Col md="8">
                <Form.Control
                    value={props.offer.orderComments}
                    onChange={ev => props.onOfferUpdated({ orderComments: ev.target.value })}>
                </Form.Control>
            </Col>
        </Form.Group>
    </>;
}

export default OfferHedgeSettings;